/**
 * Pure derivation of the user's effective subscription state.
 *
 * This is the single place where billing inputs from three sources are
 * reconciled into one answer for "what tier is this user, and why":
 *  - the user_subscriptions_v2 row (Stripe / PayPal, via Supabase)
 *  - the RevenueCat SDK snapshot (native builds)
 *  - the server-synced native subscription state
 *  - profiles.subscription_tier (comp grants, lifetime, webhook-synced tier)
 *
 * No IO, no React, no Date.now() (the clock is injected) — every billing
 * precedence rule here is unit-testable in isolation.
 */

import {
  type BillingTier,
  type SubscriptionInfo,
  type SubscriptionRecord,
  type SubscriptionStatus,
  type SubscriptionTier,
  hasFutureIsoDate,
  hasPremiumAccess,
  normalizeBillingTier,
  normalizeSubscriptionStatus,
  normalizeSubscriptionTier,
} from './types';

/** Structural shape of the RevenueCat SDK snapshot the derivation reads. */
export interface NativeSnapshotLike {
  isActive: boolean;
  tier: string | null;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  billingTier: BillingTier | null;
  managementUrl: string | null;
  store: string | null;
  productIdentifier: string | null;
  periodType?: string | null;
}

/** Structural shape of the sync-native-subscription Edge Function response. */
export interface NativeSyncedLike {
  is_active: boolean;
  status: string | null;
  tier: string | null;
  billing_tier: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  management_url: string | null;
  store: string | null;
  product_identifier: string | null;
  preserved_stripe_subscription?: boolean;
  preserved_paypal_subscription?: boolean;
  has_direct_native_access?: boolean;
  subscription_override?: boolean;
  lifetime_member?: boolean;
}

export interface DeriveSubscriptionStateInput {
  subscriptionRecord: SubscriptionRecord | null | undefined;
  nativeSnapshot: NativeSnapshotLike;
  nativeSynced: NativeSyncedLike | null;
  /** Raw profiles.subscription_tier value. */
  profileSubscriptionTier: string | null | undefined;
  nativeBillingEnabled: boolean;
  /** Lazily resolves the App Store / Play Store management URL. */
  getNativeStoreManagementUrlFallback: () => string | null;
  paypalManageUrlFallback: string;
  /** Injected clock for deterministic tests. */
  now?: number;
}

export interface DerivedSubscriptionState {
  subscriptionInfo: SubscriptionInfo;
  isPremium: boolean;
  isLifetime: boolean;
  isTrialing: boolean;
  hasActiveBillingStatus: boolean;
  hasBillingAccess: boolean;
  hasPremiumAccessResolved: boolean;
  preservedStripeSubscription: boolean;
  preservedPayPalSubscription: boolean;
  isLegacyWebSubscriber: boolean;
  nativeIsActive: boolean;
}

const APP_OR_PLAY_STORES = new Set(['APP_STORE', 'MAC_APP_STORE', 'PLAY_STORE']);

export function isAppOrPlayStore(store: string | null | undefined): boolean {
  return Boolean(store) && APP_OR_PLAY_STORES.has(String(store).toUpperCase());
}

export function deriveSubscriptionState({
  subscriptionRecord,
  nativeSnapshot,
  nativeSynced,
  profileSubscriptionTier,
  nativeBillingEnabled,
  getNativeStoreManagementUrlFallback,
  paypalManageUrlFallback,
  now = Date.now(),
}: DeriveSubscriptionStateInput): DerivedSubscriptionState {
  const preservedStripeSubscription = Boolean(nativeSynced?.preserved_stripe_subscription);
  const preservedPayPalSubscription = Boolean(nativeSynced?.preserved_paypal_subscription);
  const preservedWebSubscription = preservedStripeSubscription || preservedPayPalSubscription;
  const normalizedBillingProvider = subscriptionRecord?.billing_provider?.toLowerCase() || null;
  const webBillingProvider: Extract<SubscriptionInfo['provider'], 'stripe' | 'paypal'> | null =
    normalizedBillingProvider === 'paypal' || Boolean(subscriptionRecord?.paypal_subscription_id)
      ? 'paypal'
      : normalizedBillingProvider === 'stripe' || Boolean(subscriptionRecord?.stripe_customer_id)
        ? 'stripe'
        : null;
  // Only a real App Store / Play Store purchase should route billing to the stores.
  // A management URL alone is not evidence of a store subscription, and RevenueCat
  // entitlements backed by web billing (e.g. STRIPE) must use the web cancel flow.
  const isWebBackedStore = (store: string | null | undefined) =>
    Boolean(store) && !isAppOrPlayStore(store);
  const snapshotIsStoreBacked =
    isAppOrPlayStore(nativeSnapshot.store) ||
    (!isWebBackedStore(nativeSnapshot.store) &&
      (nativeSnapshot.isActive || Boolean(nativeSnapshot.productIdentifier)));
  const syncedIsStoreBacked =
    isAppOrPlayStore(nativeSynced?.store) ||
    (!isWebBackedStore(nativeSynced?.store) && Boolean(nativeSynced?.product_identifier));
  const hasNativeStoreProviderSignal =
    normalizedBillingProvider === 'native_store' || snapshotIsStoreBacked || syncedIsStoreBacked;
  const isLegacyWebSubscriber =
    nativeBillingEnabled &&
    Boolean(webBillingProvider) &&
    (!hasNativeStoreProviderSignal || preservedWebSubscription);

  const rawScheduledTier = normalizeBillingTier(subscriptionRecord?.scheduled_subscription_type);
  const rawScheduledChangeAt = subscriptionRecord?.scheduled_change_at || null;
  const rawScheduledTimestamp = rawScheduledChangeAt ? Date.parse(rawScheduledChangeAt) : NaN;
  const hasValidScheduledTimestamp =
    Number.isFinite(rawScheduledTimestamp) && rawScheduledTimestamp > now;
  const scheduledBillingTier =
    nativeBillingEnabled
      ? null
      : rawScheduledTier &&
          hasValidScheduledTimestamp &&
          rawScheduledTier !== normalizeBillingTier(subscriptionRecord?.subscription_type)
        ? rawScheduledTier
        : null;
  const scheduledBillingChangeAt = scheduledBillingTier ? rawScheduledChangeAt : null;
  const normalizedBillingStatus = subscriptionRecord?.status?.trim().toLowerCase() || null;
  const stripeStatus = normalizeSubscriptionStatus(normalizedBillingStatus);
  const stripePeriodEnd = subscriptionRecord?.current_period_end || null;
  const hasStripeAccessFromStatus =
    stripeStatus === 'active' ||
    stripeStatus === 'trialing';
  const hasExplicitCanceledBillingStatus =
    Boolean(normalizedBillingStatus) &&
    !hasStripeAccessFromStatus;
  const hasStripeAccessFromPeriod =
    !stripeStatus &&
    Boolean(subscriptionRecord?.stripe_customer_id) &&
    hasFutureIsoDate(stripePeriodEnd, now);
  const snapshotTier = normalizeSubscriptionTier(nativeSnapshot.tier);
  const syncedTier = normalizeSubscriptionTier(nativeSynced?.tier);
  const snapshotPeriodStillActive =
    !nativeSnapshot.currentPeriodEnd || hasFutureIsoDate(nativeSnapshot.currentPeriodEnd, now);
  const snapshotHasNativeAccess =
    (nativeSnapshot.isActive || snapshotTier === 'premium' || snapshotTier === 'trialing') &&
    snapshotPeriodStillActive;
  const syncedHasNativeAccess =
    Boolean(nativeSynced?.is_active) || syncedTier === 'premium' || syncedTier === 'trialing';
  const preferSnapshotNativeAccess =
    nativeBillingEnabled && snapshotHasNativeAccess && !syncedHasNativeAccess;

  const nativeStatus = normalizeSubscriptionStatus(
    preferSnapshotNativeAccess ? nativeSnapshot.status : nativeSynced?.status || nativeSnapshot.status
  );
  const nativeIsActive = preferSnapshotNativeAccess
    ? snapshotHasNativeAccess
    : Boolean(nativeSynced?.is_active ?? nativeSnapshot.isActive);
  const effectiveStatus: SubscriptionStatus =
    nativeBillingEnabled
      ? preservedStripeSubscription
        ? stripeStatus ?? nativeStatus
        : nativeStatus ?? stripeStatus
      : stripeStatus;
  const profileTier = normalizeSubscriptionTier(profileSubscriptionTier);
  const nativeTier = preferSnapshotNativeAccess ? snapshotTier : syncedTier ?? snapshotTier;
  const inferredTierFromStripeStatus: SubscriptionTier | null =
    stripeStatus === 'trialing'
      ? 'trialing'
      : stripeStatus === 'active'
        ? 'premium'
        : null;
  const inferredTierFromStripeAccess: SubscriptionTier | null =
    inferredTierFromStripeStatus ??
    (hasStripeAccessFromPeriod ? 'premium' : null);
  const hasExplicitInactiveWebStatus =
    Boolean(webBillingProvider) && hasExplicitCanceledBillingStatus;
  const hasDirectNativeAccess =
    snapshotHasNativeAccess || Boolean(nativeSynced?.has_direct_native_access);
  const hasProtectedProfileAccess =
    profileTier === 'lifetime' ||
    Boolean(nativeSynced?.subscription_override) ||
    Boolean(nativeSynced?.lifetime_member);
  const shouldSuppressStaleProfileTier =
    nativeBillingEnabled &&
    hasExplicitInactiveWebStatus &&
    !hasDirectNativeAccess &&
    !hasProtectedProfileAccess;

  let tier: SubscriptionTier = 'free';
  if (profileTier === 'lifetime') {
    tier = 'lifetime';
  } else if (nativeBillingEnabled) {
    const profileNativeFallbackTier =
      profileTier && profileTier !== 'free' ? profileTier : null;

    if (preservedStripeSubscription) {
      tier =
        profileTier && profileTier !== 'free'
          ? profileTier
          : inferredTierFromStripeAccess ?? 'free';
    } else if (nativeTier && nativeTier !== 'free') {
      tier = nativeTier;
    } else if (profileNativeFallbackTier && !shouldSuppressStaleProfileTier) {
      // Native purchase state can lag briefly across SDK/server sync boundaries.
      // If the profile has already been synced to a paid tier, do not gate
      // unless an explicit inactive web record is the only access signal.
      tier = profileNativeFallbackTier;
    } else {
      tier = 'free';
    }
  } else {
    // An explicit paid profile tier wins over a dead billing record: the
    // webhook resets profiles.subscription_tier to 'free' on cancellation for
    // everyone except subscription_override / lifetime users, so a 'premium'
    // profile alongside a canceled Stripe row means a deliberate comp grant.
    // The canceled status only suppresses tier inference from billing data.
    tier = profileTier && profileTier !== 'free'
      ? profileTier
      : hasExplicitCanceledBillingStatus
        ? 'free'
        : inferredTierFromStripeStatus ?? 'free';
  }

  const currentPeriodEnd =
    nativeBillingEnabled
      ? preferSnapshotNativeAccess
        ? nativeSnapshot.currentPeriodEnd ?? nativeSynced?.current_period_end ?? subscriptionRecord?.current_period_end ?? null
        : nativeSynced?.current_period_end ?? nativeSnapshot.currentPeriodEnd ?? subscriptionRecord?.current_period_end ?? null
      : subscriptionRecord?.current_period_end || null;
  const cancelAtPeriodEnd =
    nativeBillingEnabled
      ? preferSnapshotNativeAccess
        ? Boolean(nativeSnapshot.cancelAtPeriodEnd || nativeSynced?.cancel_at_period_end)
        : Boolean(nativeSynced?.cancel_at_period_end ?? nativeSnapshot.cancelAtPeriodEnd)
      : Boolean(subscriptionRecord?.cancel_at_period_end);
  const billingTier =
    nativeBillingEnabled
      ? preferSnapshotNativeAccess
        ? nativeSnapshot.billingTier ?? normalizeBillingTier(nativeSynced?.billing_tier) ?? normalizeBillingTier(subscriptionRecord?.subscription_type)
        : normalizeBillingTier(nativeSynced?.billing_tier) ?? nativeSnapshot.billingTier ?? normalizeBillingTier(subscriptionRecord?.subscription_type)
      : normalizeBillingTier(subscriptionRecord?.subscription_type);
  const provider: SubscriptionInfo['provider'] =
    nativeBillingEnabled
      ? hasNativeStoreProviderSignal && !preservedWebSubscription
        ? 'native_store'
        : webBillingProvider ?? 'none'
      : webBillingProvider ?? 'none';
  const managementUrl =
    provider === 'native_store'
      ? preferSnapshotNativeAccess
        ? nativeSnapshot.managementUrl || nativeSynced?.management_url || getNativeStoreManagementUrlFallback()
        : nativeSynced?.management_url || nativeSnapshot.managementUrl || getNativeStoreManagementUrlFallback()
      : provider === 'paypal'
        ? subscriptionRecord?.paypal_manage_url || paypalManageUrlFallback
      : null;

  const subscriptionInfo: SubscriptionInfo = {
    tier,
    status: effectiveStatus,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    stripeCustomerId: subscriptionRecord?.stripe_customer_id || null,
    billingTier,
    scheduledBillingTier,
    scheduledBillingChangeAt,
    provider,
    managementUrl,
    store:
      nativeBillingEnabled
        ? preferSnapshotNativeAccess
          ? nativeSnapshot.store || nativeSynced?.store || null
          : nativeSynced?.store || nativeSnapshot.store
        : null,
    productIdentifier:
      nativeBillingEnabled
        ? preferSnapshotNativeAccess
          ? nativeSnapshot.productIdentifier || nativeSynced?.product_identifier || null
          : nativeSynced?.product_identifier || nativeSnapshot.productIdentifier || null
        : null,
    // Period type comes only from an active snapshot. An expired entitlement
    // keeps its old INTRO/TRIAL on the SDK object, and copying that while the
    // server sync supplies the current purchase labels a new paid purchase
    // as a trial. The server has no period type of its own, so an active
    // snapshot still supplies it when the server wins the period end.
    periodType:
      nativeBillingEnabled && snapshotHasNativeAccess
        ? (nativeSnapshot.periodType ?? null)
        : null,
  };

  const isPremium = hasPremiumAccess(subscriptionInfo.tier);
  const isLifetime = subscriptionInfo.tier === 'lifetime';
  const isTrialing =
    subscriptionInfo.tier === 'trialing' || subscriptionInfo.status === 'trialing';
  const hasActiveBillingStatus = hasStripeAccessFromStatus;
  const hasBillingAccess =
    isPremium ||
    isLifetime ||
    (nativeBillingEnabled
      ? (preservedWebSubscription && hasStripeAccessFromStatus) || nativeIsActive
      : hasActiveBillingStatus);
  const hasPremiumAccessResolved = isPremium || hasBillingAccess;

  return {
    subscriptionInfo,
    isPremium,
    isLifetime,
    isTrialing,
    hasActiveBillingStatus,
    hasBillingAccess,
    hasPremiumAccessResolved,
    preservedStripeSubscription,
    preservedPayPalSubscription,
    isLegacyWebSubscriber,
    nativeIsActive,
  };
}
